export type KishuStatsPayload = {
  burnSupply: number
  circulatingSupply: string
  dailyTransfers: number
  dailyVolume: number
  holders: number
  maxSupply: number
  kishuPriceEth: number
  kishuPriceUsdt: number
  monthlyTransfers: number
  monthlyVolume: number
  timestamp: string
  totalSupply: string
  totalTransfers: number
  totalVolume: number
  weeklyTransfers: number
  weeklyVolume: number
}

const KISHU_DECIMAL_FACTOR = 1000000000
const BILLION = 1000000000

export async function fetchKishuStats(): Promise<KishuStatsPayload> {
  const statsUrl = process.env.REACT_APP_KISHU_STATS_URL
  const statsToken = process.env.REACT_APP_KISHU_STATS_TOKEN

  if (!statsUrl) throw new Error('Missing REACT_APP_KISHU_STATS_URL')

  const response = await fetch(statsUrl, {
    headers: statsToken ? { authorization: `Bearer ${statsToken}` } : undefined
  })
  if (!response.ok) throw new Error(`Stats request failed: ${response.status}`)

  return response.json()
}

export function toNumber(value: number | string | undefined | null): number {
  const number = Number(value || 0)
  return Number.isFinite(number) ? number : 0
}

export function burnSupplyAsKishu(stats: KishuStatsPayload): number {
  return toNumber(stats.burnSupply) / KISHU_DECIMAL_FACTOR
}

export function asBillionsKishu(kishuAmount: number): number {
  return kishuAmount / BILLION
}

export function pricePerBillionKishu(stats: KishuStatsPayload): number {
  return toNumber(stats.kishuPriceUsdt) * BILLION
}

export function formatTokenAmount(value: number | string, maximumFractionDigits = 2): string {
  const number = toNumber(value)
  if (!number) return '0'
  return new Intl.NumberFormat('en-US', { maximumFractionDigits }).format(number)
}

export function formatPrecise(value: number | string): string {
  const number = toNumber(value)
  if (!number) return '0'
  return number < 0.000001 && number > 0 ? number.toExponential(4) : formatTokenAmount(number, 6)
}

export function formatUsd(value: number | string, maximumFractionDigits = 2): string {
  const number = toNumber(value)
  if (!number) return '$0'
  return `$${new Intl.NumberFormat('en-US', {
    maximumFractionDigits,
    minimumFractionDigits: number < 1 ? Math.min(maximumFractionDigits, 4) : 0
  }).format(number)}`
}

export function formatPercent(value: number | string, maximumFractionDigits = 4): string {
  return `${formatTokenAmount(toNumber(value), maximumFractionDigits)}%`
}
